/**
 * Checkout handoff — the ONE way GLORB sends a shopper to Shopify checkout.
 *
 * Used by the cart drawer (Cart.svelte) and the product page's Buy now
 * (ProductTemplate.svelte). The two had grown independent copies of the same
 * 50 lines: host rewrite, consent-gated UTMs, promo code, InitiateCheckout,
 * checkout_initiated, markCheckoutInProgress, navigate. In flossr the second
 * copy rotted into a bare `location.href = checkoutUrl`, which dropped the
 * earned discount, skipped Meta InitiateCheckout and — because it never called
 * markCheckoutInProgress() — fired a false `cart_abandoned` on every
 * successful checkout. One module so that cannot happen here.
 *
 * Order matters:
 *   1. refuse blocked carts → checkout_handoff_failed, a message for the UI
 *      (a cart that could not be read is NOT an empty cart — see
 *      checkoutBlockReason)
 *   2. refuse a missing checkoutUrl the same way, never a silent no-op
 *   3. parse the checkout URL — still no side effects, so a bad URL is
 *      recoverable
 *   4. only then commit: markCheckoutInProgress, InitiateCheckout,
 *      checkout_initiated
 *   5. take the promo code LAST. takePromoCode() consumes it, so it must not
 *      run on a path that can still fail — a burned code the shopper never
 *      got to use is worse than no code. Nor on a pack size the campaign's
 *      code skips (the 4-pack): Shopify refuses it there, so it stays held
 *      for an order it does apply to (ledger 2026-10-14-108).
 *
 * Two callers, two cart sources, deliberately:
 *   - the drawer refetches the persistent cart and passes it in (with the
 *     store's `error` so an unreadable cart is told apart from an empty one)
 *   - Buy now passes the cart the add mutation just returned. It must NOT be
 *     refetched: reading it back was a read-after-write against Shopify that
 *     cost 24 checkouts (d42eddf). This module never fetches; resolving the
 *     cart is the caller's job.
 */
import { browser } from "$app/environment";
import { trackInitiateCheckout } from "#lib/utils/analytics.js";
import { captureBeacon } from "#lib/utils/posthog.js";
import { markCheckoutInProgress } from "#lib/utils/engagement.js";
import {
  checkoutBlockReason,
  cartUnitCount,
} from "#lib/utils/cart-quantity.js";
import { takePromoCode, peekPromoCode } from "#lib/utils/promo-code.js";
import { activeCampaign, excludesQty } from "#lib/campaign.js";

// GLORB's Shopify store domain. Shopify sometimes hands back a checkoutUrl on
// a custom domain that is not configured for checkout.
export const CHECKOUT_HOST = "e1d9f8-a7.myshopify.com";

const GENERIC_ERROR = "Unable to proceed to checkout";

function defaultNavigate(url) {
  window.location.href = url;
}

function nowMs() {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/**
 * The drawer's cart calls its lines `items`; the cart returned by cartCreate /
 * cartLinesAdd calls them `lines`. Both shapes reach lineQuantity fine.
 */
function cartLines(cart) {
  if (Array.isArray(cart?.items)) return cart.items;
  if (Array.isArray(cart?.lines)) return cart.lines;
  return [];
}

/**
 * Pure: turn Shopify's checkoutUrl into the URL we actually send people to.
 * Shopify's own params (key=…) are kept.
 *
 * No `?country=` hint: the buyer country lives on the cart itself via
 * setCartBuyerIdentity, which is what Markets reads. Shopify's checkout does
 * not honour the query-string version.
 *
 * @param {object} opts
 * @param {string} opts.checkoutUrl
 * @param {boolean} [opts.marketingConsent]
 * @param {URLSearchParams|null} [opts.utmParams]
 * @param {string} [opts.discountCode]
 */
export function buildCheckoutUrl({
  checkoutUrl,
  marketingConsent = false,
  utmParams = null,
  discountCode = "",
}) {
  const url = new URL(checkoutUrl);
  if (!url.hostname.includes("myshopify.com")) url.hostname = CHECKOUT_HOST;

  if (marketingConsent && utmParams) {
    utmParams.forEach((value, key) => url.searchParams.set(key, value));
  }
  if (discountCode) url.searchParams.set("discount", discountCode);

  return url.toString();
}

/**
 * Hand a cart off to Shopify checkout.
 *
 * @param {object} opts
 * @param {'cart_drawer'|'product_page'} opts.source where the click came from
 * @param {object} opts.cart  { items | lines, checkoutUrl, error? }
 * @param {(url:string)=>void} [opts.navigate] injectable for tests
 * @returns {Promise<{ok:true,url:string}|{ok:false,error:string,reason:string}>}
 */
export async function startCheckout({
  source,
  cart,
  navigate = defaultNavigate,
}) {
  const t0 = nowMs();
  const lines = cartLines(cart);

  const fail = (reason, message) => {
    captureBeacon("checkout_handoff_failed", {
      reason,
      source,
      duration_ms: Math.round(nowMs() - t0),
    });
    return { ok: false, error: message, reason };
  };

  // The cart knows its own buyer country (setCartBuyerIdentity wrote it), so
  // the blocked-checkout refusal can say the same thing the add-to-cart one
  // does. When it does not, the generic sentence stands.
  const blocked = checkoutBlockReason({
    items: lines,
    error: cart?.error,
    country: cart?.buyerIdentity?.countryCode ?? cart?.country ?? undefined,
  });
  if (blocked) return fail(blocked.reason, blocked.message);

  // The shopper gets the generic line, not "No checkout URL available" — the
  // drawer used to show that verbatim. `reason` keeps the detail for PostHog.
  if (!cart?.checkoutUrl) return fail("no_checkout_url", GENERIC_ERROR);

  const marketingConsent =
    browser && localStorage.getItem("marketing") === "true";
  const utmParams =
    marketingConsent &&
    typeof window !== "undefined" &&
    typeof window.getUTMParams === "function"
      ? window.getUTMParams()
      : null;

  // Parsed before anything commits, so a malformed checkoutUrl does not burn
  // the promo code or leave the abandonment tracker suppressed.
  let baseUrl;
  try {
    baseUrl = buildCheckoutUrl({
      checkoutUrl: cart.checkoutUrl,
      marketingConsent,
      utmParams,
    });
  } catch {
    return fail("bad_checkout_url", GENERIC_ERROR);
  }

  // From here on the checkout is happening: tell the abandonment tracker
  // before anything that could unload the page.
  const unitCount = cartUnitCount(lines);

  markCheckoutInProgress();
  trackInitiateCheckout(lines);
  captureBeacon("checkout_initiated", {
    source,
    unit_count: unitCount,
    line_count: lines.length,
    duration_ms: Math.round(nowMs() - t0),
  });

  // The 4-pack's own discount combines with free shipping only, so Shopify
  // refuses the survey code on it. Handing it over anyway sent a shopper round
  // the checkout seven times and started the code's window on an order it
  // could never apply to. Every other cart takes the path below unchanged.
  if (excludesQty(activeCampaign(), unitCount)) {
    if (peekPromoCode()) {
      captureBeacon("discount_code_withheld", {
        reason: "pack_excluded",
        unit_count: unitCount,
        source,
      });
    }
    navigate(baseUrl);
    return { ok: true, url: baseUrl };
  }

  // Last, because it consumes the code. takePromoCode drops it once its grace
  // window has passed, so an earned code no longer rides along on every future
  // checkout in this browser.
  const { code: promoCode, expired: promoExpired } = takePromoCode();
  if (promoCode) {
    captureBeacon("discount_code_applied", {
      code: promoCode,
      source: "auto_localstorage",
    });
  } else if (promoExpired) {
    captureBeacon("discount_code_expired", { source });
  }

  const url = promoCode
    ? buildCheckoutUrl({
        checkoutUrl: cart.checkoutUrl,
        marketingConsent,
        utmParams,
        discountCode: promoCode,
      })
    : baseUrl;

  navigate(url);
  return { ok: true, url };
}
